<template>
    <div class="album-detail-page">
        <!-- Header Section -->
        <div class="header-section">
            <div class="cover-wrapper">
                <img :src="album.coverImage || defaultCover" alt="cover" class="album-cover" />
                <div class="cover-tag">精品专辑</div>
            </div>
            <div class="info-content">
                <h1 class="album-title">
                    <el-tag type="danger" effect="dark" size="small" class="badge">精品</el-tag>
                    {{ album.title }}
                    <el-icon class="hot-icon" color="#f56c6c">
                        <StarFilled />
                    </el-icon>
                </h1>

                <div class="desc-box" :class="{ 'collapsed': descCollapsed }">
                    <span class="label">简介：</span>
                    <span class="text">{{ album.description || '暂无简介' }}</span>
                    <span class="toggle-btn" @click="descCollapsed = !descCollapsed"
                        v-if="album.description && album.description.length > 100">
                        {{ descCollapsed ? '查看更多' : '收起' }}
                    </span>
                </div>

                <div class="meta-row">
                    <span class="meta-item"><el-icon>
                            <Timer />
                        </el-icon> {{ formatDate(album.CreatedAt) }}</span>
                    <span class="meta-item"><el-icon>
                            <Refresh />
                        </el-icon> 更新中</span>
                    <span class="meta-item"><el-icon>
                            <View />
                        </el-icon> {{ album.views || 0 }}次浏览</span>
                    <span class="meta-item"><el-icon>
                            <Download />
                        </el-icon> {{ album.downloads || 0 }}次下载</span>
                    <span class="meta-item"> © {{ new Date().getFullYear() }}年，{{ album.province || '全国' }}，数学</span>
                </div>

                <div class="action-row">
                    <el-button round icon="Share">分享</el-button>
                    <el-button round icon="Warning">挑错</el-button>
                    <el-button round icon="Star" type="primary" plain>收藏</el-button>
                </div>
            </div>
            <img src="@/assets/logo.png" class="watermark-seal" alt="seal" v-if="false" /> <!-- Optional seal -->
        </div>

        <!-- Main Body -->
        <div class="album-body">
            <!-- Left Content: Resource List -->
            <div class="left-content">
                <div class="list-header">
                    <div class="sort-label">资料名称 <el-icon>
                            <Sort />
                        </el-icon></div>
                    <div class="header-right">
                        <span class="col-date">更新时间</span>
                        <span class="col-price">价格</span>
                        <span class="col-downloads">下载量</span>
                        <span class="col-actions">操作</span>
                    </div>
                </div>

                <div class="toolbar">
                    <el-checkbox v-model="checkAll" :indeterminate="isIndeterminate"
                        @change="handleCheckAllChange">全选</el-checkbox>
                    <div class="toolbar-right">
                        <span class="selected-count">已选 {{ selectedPapers.length }} 份资料</span>
                        <el-button size="small" type="primary" icon="Download" :disabled="selectedPapers.length === 0"
                            @click="handleBatchDownload">批量下载</el-button>
                    </div>
                </div>

                <div class="chapter-list" v-loading="loading">
                    <div v-for="chapter in chapters" :key="chapter.id" class="chapter-block">
                        <div class="chapter-title" @click="chapter.expanded = !chapter.expanded">
                            <el-checkbox v-model="chapter.checked" @click.stop
                                @change="(val) => handleChapterCheck(val, chapter)"></el-checkbox>
                            <span class="chapter-icon"><el-icon>
                                    <FolderOpened v-if="chapter.expanded" />
                                    <Folder v-else />
                                </el-icon></span>
                            <span class="title-text">{{ chapter.label }}</span>
                            <span class="chapter-count">{{ (chapter.children || []).length }} 份</span>
                            <el-icon class="chapter-chevron" :class="{ 'is-open': chapter.expanded }">
                                <ArrowRight />
                            </el-icon>
                        </div>

                        <el-collapse-transition>
                            <div class="resource-list" v-show="chapter.expanded">
                                <AlbumStructureNode v-for="child in chapter.children" :key="child.id" :node="child"
                                    @node-change="updateCheckAllState" @view-paper="viewPaper" />
                                <div v-if="!chapter.children || chapter.children.length === 0" class="empty-chapter">
                                    暂无资料
                                </div>
                            </div>
                        </el-collapse-transition>
                    </div>
                    <el-empty v-if="chapters.length === 0 && !loading" description="暂无内容" />
                </div>
            </div>

            <!-- Right Sidebar -->
            <div class="right-sidebar">
                <!-- Author Card -->
                <div class="sidebar-card author-card">
                    <div class="author-header">
                        <el-avatar :size="50"
                            :src="album.authorAvatar || 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png'" />
                        <div class="author-info">
                            <div class="author-name">{{ album.authorName || '刷题网官方' }}</div>
                            <el-tag size="small" effect="plain">{{ album.authorDescription || '官方认证' }}</el-tag>
                        </div>
                    </div>
                    <div class="author-stats">
                        <div class="stat-box">
                            <div class="num">1.2万</div>
                            <div class="txt">资料</div>
                        </div>
                        <div class="stat-box">
                            <div class="num">661</div>
                            <div class="txt">专辑</div>
                        </div>
                        <div class="stat-box">
                            <div class="num">64.5万</div>
                            <div class="txt">销量</div>
                        </div>
                    </div>
                    <div class="author-actions">
                        <el-button type="primary" plain @click="handleViewMore">查看更多</el-button>
                        <el-button type="primary" @click="handleFollowAuthor">关注作者</el-button>
                    </div>
                </div>

                <!-- Related Recommend -->
                <div class="sidebar-card recommend-card">
                    <div class="card-title">
                        <span class="bar"></span>相关推荐
                        <el-button link class="more-btn">换一批</el-button>
                    </div>
                    <div class="recommend-list" v-loading="relatedLoading">
                        <div v-for="item in relatedAlbums" :key="item.id" class="recommend-item"
                            @click="viewAlbum(item.id)">
                            <div class="cover">
                                <img v-if="item.coverImage" :src="item.coverImage" class="album-cover"
                                    style="width:100%;height:100%;object-fit:cover;border-radius:6px;">
                                <span v-else class="tag">推荐</span>
                            </div>
                            <div class="info">
                                <div class="rec-title">{{ item.title }}</div>
                                <div class="rec-meta">
                                    <span>{{ item.province || '全国' }}</span>
                                    <span style="margin-left: 8px">{{ item.views }}浏览</span>
                                </div>
                            </div>
                        </div>
                        <el-empty v-if="relatedAlbums.length === 0" description="暂无推荐" image-size="60" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import AlbumStructureNode from '@/components/AlbumStructureNode.vue'
import { useRoute, useRouter } from 'vue-router'
import { getAlbumDetail, getRelatedAlbums, batchDownloadPapers } from '@/utils/api'
import {
    StarFilled, Timer, Refresh, View, Download, Share, Warning, Star,
    Sort, ShoppingCart, Document, Folder, FolderOpened, ArrowRight
} from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'

const route = useRoute()
const router = useRouter()
const albumId = route.params.id

const album = ref({})
const chapters = ref([]) // Flattened or processed tree structure
const loading = ref(false)
const descCollapsed = ref(true)
const defaultCover = 'https://picsum.photos/300/400?random=1'

// Mock Data deleted

// Selection State
const checkAll = ref(false)
const isIndeterminate = ref(false)

const selectedPapers = computed(() => {
    // Re-use helper
    const all = getAllPapersRecursive(chapters.value)
    return all.filter(p => p.checked)
})

const relatedAlbums = ref([])
const relatedLoading = ref(false)

onMounted(() => {
    fetchData(route.params.id)
})

watch(() => route.params.id, (newId) => {
    if (newId) {
        fetchData(newId)
    }
})

const fetchData = async (id) => {
    loading.value = true
    try {
        const res = await getAlbumDetail(id)
        album.value = res.data.album
        const rawTree = res.data.tree || []
        processChapters(rawTree)

        // Fetch related
        fetchRelated(id)
    } catch (error) {
        ElMessage.error('加载失败')
        console.error(error)
    } finally {
        loading.value = false
    }
}

const fetchRelated = async (id) => {
    relatedLoading.value = true
    try {
        const res = await getRelatedAlbums(id)
        relatedAlbums.value = res.data
    } catch (err) {
        console.error(err)
    } finally {
        relatedLoading.value = false
    }
}

const processChapters = (nodes) => {
    let result = []
    let rootPapers = []

    nodes.forEach(node => {
        if (node.type === 'folder') {
            // It's a chapter
            // We need to flatten ALL papers inside this folder distinctively?
            // Or just use direct children. For now, let's use direct children or 1 level deep.
            // If the folder has subfolders, we might need to decide how to show them.
            // Current simplistic approach: Flatten all papers under this chapter recursively?
            // Or just leave it as hierarchical but displayed flat-ish.
            // Let's recursively check checkbox state.

            // Let's just take the node as is, but ensure 'checked' property exists.

            // Allow sub-folders to just be visualized as indented items? 
            // Design says "Flat list of chapters". Let's assume 2-level structure.
            // If node has children that are folders, maybe we shouldn't flatten them too much.
            // We want to preserve hierarchy now for Sub-Chapters
            // So we use a recursive map instead of flattenPapers
            const processedNode = {
                ...node,
                checked: false,
                expanded: true,
                children: processNodesRecursive(node.children || [])
            }
            result.push(processedNode)
        } else {
            // It's a paper at root
            rootPapers.push({ ...node, checked: false })
        }
    })

    if (rootPapers.length > 0) {
        result.unshift({
            id: 'root_default',
            label: '默认分组',
            type: 'folder',
            checked: false,
            expanded: true,
            children: rootPapers
        })
    }

    chapters.value = result
}

// Helper to process nodes recursively without flattening
const processNodesRecursive = (nodes) => {
    return nodes.map(node => {
        return {
            ...node,
            checked: false,
            expanded: true,
            children: node.children ? processNodesRecursive(node.children) : []
        }
    })
}

const handleCheckAllChange = (val) => {
    checkAll.value = val
    isIndeterminate.value = false
    chapters.value.forEach(chap => {
        chap.checked = val
        if (chap.children) {
            chap.children.forEach(p => p.checked = val)
        }
    })
}

const handleChapterCheck = (val, chapter) => {
    toggleChildrenRecursive(chapter, val)
    updateCheckAllState()
}

const toggleChildrenRecursive = (node, checked) => {
    if (node.children) {
        node.children.forEach(child => {
            child.checked = checked
            toggleChildrenRecursive(child, checked)
        })
    }
}

const handlePaperCheck = () => {
    // Check if chapter should be checked
    chapters.value.forEach(chap => {
        if (chap.children && chap.children.length > 0) {
            const allChecked = chap.children.every(p => p.checked)
            const someChecked = chap.children.some(p => p.checked)
            chap.checked = allChecked
            // element-plus checkbox doesn't strictly support indeterminate via model on parent easily this way without mappedprop
            // but for simple logic:
        }
    })
    updateCheckAllState()
}

const updateCheckAllState = () => {
    const allPapers = getAllPapersRecursive(chapters.value)
    if (allPapers.length === 0) return

    const checkedCount = allPapers.filter(p => p.checked).length
    checkAll.value = checkedCount === allPapers.length
    isIndeterminate.value = checkedCount > 0 && checkedCount < allPapers.length
}

const getAllPapersRecursive = (nodes) => {
    let papers = []
    nodes.forEach(node => {
        if (node.type === 'paper') {
            papers.push(node)
        } else if (node.children) {
            papers.push(...getAllPapersRecursive(node.children))
        }
    })
    return papers
}

const viewPaper = (paperId) => {
    const routeData = router.resolve({ path: `/papers/${paperId}` });
    window.open(routeData.href, '_blank');
}

const formatDate = (dateStr, showDateOnly = false) => {
    if (!dateStr) return '-'
    const d = new Date(dateStr)
    if (showDateOnly) return d.toLocaleDateString()
    return d.toLocaleString()
}
const viewAlbum = (id) => {
    router.push(`/albums/${id}`)
}

const handleViewMore = () => {
    ElMessage.info('该作者更多作品正在整理中...')
}

const handleFollowAuthor = () => {
    ElMessage.success('关注成功！')
}

const handleBatchDownload = async () => {
    if (selectedPapers.value.length === 0) return
    const paperIds = selectedPapers.value.map(p => p.id)
    try {
        ElMessage.info('正在打包下载，请稍候...')
        const response = await batchDownloadPapers({ paperIds })

        // Create blob link to download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'papers_batch.zip');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error(error)
        ElMessage.error('下载失败')
    }
}

</script>

<style scoped>
.album-detail-page {
    max-width: 1200px;
    margin: 20px auto;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    color: #333;
}

/* Header Section - Modern Style */
.header-section {
    background: #fff;
    padding: 30px;
    display: flex;
    gap: 30px;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
    position: relative;
    margin-bottom: 30px;
    border: 1px solid #f0f0f0;
}

.cover-wrapper {
    width: 200px;
    height: 260px;
    position: relative;
    flex-shrink: 0;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
}

.album-cover {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s;
}

.cover-wrapper:hover .album-cover {
    transform: scale(1.05);
}

.cover-tag {
    position: absolute;
    top: 10px;
    left: -30px;
    background: #f56c6c;
    color: #fff;
    font-size: 12px;
    padding: 4px 30px;
    transform: rotate(-45deg);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.info-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
}

.album-title {
    font-size: 24px;
    font-weight: 700;
    color: #2c3e50;
    margin: 0 0 16px 0;
    display: flex;
    align-items: center;
    gap: 12px;
    line-height: 1.4;
}

.badge {
    border-radius: 4px;
    font-weight: normal;
}

.desc-box {
    background: #f8f9fa;
    padding: 16px;
    border-radius: 8px;
    color: #555;
    font-size: 14px;
    line-height: 1.8;
    margin-bottom: 20px;
    position: relative;
    border-left: 4px solid #409eff;
}

.desc-box.collapsed .text {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    /* Compatibility fix */
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.toggle-btn {
    color: #409eff;
    cursor: pointer;
    margin-left: 8px;
}

.meta-row {
    font-size: 13px;
    color: #888;
    display: flex;
    gap: 24px;
    margin-bottom: 24px;
    align-items: center;
    flex-wrap: wrap;
}

.meta-item {
    display: flex;
    align-items: center;
    gap: 6px;
    background: #f5f7fa;
    padding: 6px 12px;
    border-radius: 20px;
}

.action-row {
    margin-top: auto;
    display: flex;
    gap: 12px;
}

/* Main Body */
.album-body {
    display: flex;
    gap: 30px;
}

.left-content {
    flex: 1;
}

.right-sidebar {
    width: 320px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 24px;
}

/* List Styles - Card Style */
.list-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 20px;
    background: linear-gradient(90deg, #eef4ff, #f8fbff);
    border-radius: 14px;
    font-weight: 700;
    color: #2b3950;
    font-size: 14px;
    border: 1px solid #e8eef8;
    margin-bottom: 14px;
}

.sort-label {
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
}

.header-right {
    display: flex;
    align-items: center;
    color: #64748b;
    font-weight: 600;
    font-size: 13px;
    flex-shrink: 0;
}

.header-right .col-date {
    width: 110px;
    text-align: center;
}

.header-right .col-price {
    width: 90px;
    text-align: center;
}

.header-right .col-downloads {
    width: 100px;
    text-align: center;
}

.header-right .col-actions {
    width: 96px;
    text-align: center;
}

.toolbar {
    background: #ffffff;
    padding: 12px 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border: 1px solid #e8eef8;
    border-radius: 14px;
    margin-bottom: 16px;
    box-shadow: 0 2px 10px rgba(23, 43, 77, 0.03);
}

.toolbar-right {
    display: flex;
    align-items: center;
    gap: 12px;
}

.selected-count {
    color: #999;
    font-size: 12px;
}

.chapter-block {
    background: #ffffff;
    border: 1px solid #e8eef8;
    border-radius: 14px;
    overflow: hidden;
    margin-bottom: 16px;
    box-shadow: 0 2px 12px rgba(23, 43, 77, 0.04);
}

.chapter-block:last-child {
    margin-bottom: 0;
}

.chapter-title {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 15px 20px;
    cursor: pointer;
    background: linear-gradient(90deg, #eef4ff 0%, #f7fbff 55%, #ffffff 100%);
    transition: background 0.2s ease;
}

.chapter-title:hover {
    background: linear-gradient(90deg, #e6efff 0%, #f3f9ff 55%, #ffffff 100%);
}

.chapter-icon {
    width: 30px;
    height: 30px;
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 16px;
    color: #fff;
    background: linear-gradient(135deg, #3d7eff, #6aa1ff);
    box-shadow: 0 4px 10px rgba(61, 126, 255, 0.32);
}

.title-text {
    flex: 1;
    font-weight: 700;
    font-size: 15px;
    color: #2b3950;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.chapter-count {
    flex-shrink: 0;
    font-size: 12px;
    color: #3d7eff;
    background: #e9f1ff;
    padding: 2px 10px;
    border-radius: 10px;
}

.chapter-chevron {
    flex-shrink: 0;
    color: #9aa4b8;
    transition: transform 0.25s ease;
}

.chapter-chevron.is-open {
    transform: rotate(90deg);
}

.resource-list {
    border-top: 1px solid #eef1f7;
}

.empty-chapter {
    padding: 24px 20px;
    text-align: center;
    color: #9aa4b8;
    font-size: 13px;
    background: #fbfcff;
}

/* Sidebar Styles - Refined */
.sidebar-card {
    background: #fff;
    padding: 24px;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
    border: 1px solid #f0f0f0;
}

.author-header {
    display: flex;
    gap: 16px;
    align-items: center;
    margin-bottom: 20px;
}

.author-name {
    font-weight: 700;
    font-size: 16px;
    color: #333;
    margin-bottom: 6px;
}

.author-stats {
    display: flex;
    justify-content: space-between;
    margin-bottom: 20px;
    text-align: center;
}

.stat-box .num {
    font-weight: 700;
    font-size: 18px;
    color: #333;
    margin-bottom: 4px;
}

.stat-box .txt {
    font-size: 12px;
    color: #999;
}

.author-actions {
    display: flex;
    gap: 12px;
}

.author-actions .el-button {
    flex: 1;
}

.card-title {
    font-size: 18px;
    font-weight: 700;
    color: #2c3e50;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.card-title .bar {
    display: none;
    /* Remove blue bar */
}

.recommend-item {
    display: flex;
    gap: 15px;
    margin-bottom: 20px;
    cursor: pointer;
    transition: transform 0.2s;
}

.recommend-item:hover {
    transform: translateX(4px);
}

.recommend-item .cover {
    width: 70px;
    height: 94px;
    background: #eef2f6;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #909399;
    font-size: 12px;
    flex-shrink: 0;
}

.recommend-item .info {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
}

.rec-title {
    font-size: 14px;
    color: #333;
    font-weight: 500;
    line-height: 1.5;
    margin-bottom: 8px;
}

.rec-meta {
    font-size: 12px;
    color: #999;
}
</style>
```
